import { Flex } from "@mantine/core";
import { Container } from "./StyledComponents";
import CustomSelect from "../common/Inputs/CustomSelect";
import styled, { keyframes } from "styled-components";
import { theme } from "../../theme";
import { useFilterContext } from "../../context/DashboardFilterContext";
import { useAuth } from "../../context/AuthContext";
import { getLiveTopMenu } from "../../api/dashboardApi";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FunnelIcon, ClockIcon } from "@heroicons/react/24/outline";

const pulseDot = keyframes`
  0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5); }
  60%      { box-shadow: 0 0 0 7px rgba(16,185,129,0); }
`;

const Filters = ({ flex }: { flex: any }) => {
  const { handleChange, filters }: any = useFilterContext();
  const { roleSelectData, departmentSelectData } = useAuth();
  const [data, setData] = useState<any>();

  const getTopMenuData = async () => {
    try {
      const { data, success } = await getLiveTopMenu();
      if (success) setData(data);
    } catch (error) {}
  };

  useEffect(() => {
    getTopMenuData();
  }, []);

  return (
    <Container $flex={flex}>
      <FilterRow>
        <FilterLeft>
          <FilterIconWrap>
            <FunnelIcon width={18} />
          </FilterIconWrap>
          <SelectGroup>
            <CustomSelect
              onChange={(value: any) => handleChange("department", value)}
              placeholder="Departman"
              data={departmentSelectData}
            />
            <CustomSelect
              onChange={(value: any) => handleChange("role", value)}
              placeholder="Rol"
              data={roleSelectData}
              clearable
              value={filters?.role}
            />
          </SelectGroup>
        </FilterLeft>

        <FilterRight>
          <ChipsGroup>
            <StatChip $accent="#6366F1">
              <ChipVal $accent="#6366F1">{data?.total ?? "—"}</ChipVal>
              <ChipLbl>Toplam</ChipLbl>
            </StatChip>

            <StatChip $accent="#10B981">
              <ActiveIndicator />
              <ChipVal $accent="#10B981">{data?.active ?? "—"}</ChipVal>
              <ChipLbl>Mevcut</ChipLbl>
            </StatChip>

            <StatChip $accent="#EF4444">
              <ChipVal $accent="#EF4444">{data?.late ?? "—"}</ChipVal>
              <ChipLbl>Geciken</ChipLbl>
            </StatChip>
          </ChipsGroup>

          <TimeChip>
            <ClockIcon width={18} />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <TimeLabel>Son Güncelleme</TimeLabel>
              <TimeValue>{dayjs(data?.time).format("HH:mm:ss")}</TimeValue>
            </div>
          </TimeChip>
        </FilterRight>
      </FilterRow>
    </Container>
  );
};

export default Filters;


const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const SelectGroup = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  min-width: 0;
  /* CustomSelect kendi genişliğini (w=190) satır içi stil olarak basıyor;
     bu, kutunun min-content genişliğini 190px'e çiviliyordu. */
  & > div { flex: 1; min-width: 90px; width: auto !important; }
`;

const FilterIconWrap = styled.div`
  width: 40px;
  height: 40px;
  background: ${theme.colors.brand.normal}15;
  color: ${theme.colors.brand.normal};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  @media (max-width: 600px) { display: none; }
`;

const FilterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 1200px) {
    justify-content: space-between;
  }
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ChipsGroup = styled.div`
  display: flex;
  gap: 8px;
  min-width: 0;
  @media (max-width: 768px) {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
`;


const ChipVal = styled.span<{ $accent: string }>`
  font-size: 19px; 
  font-weight: 800;
  color: ${(p) => p.$accent};
  line-height: 1;
`;

const ChipLbl = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  white-space: nowrap;
  @media (max-width: 1300px) { font-size: 10px; }
`;

const TimeLabel = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  line-height: 1;
`;

const TimeValue = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
`;

const ActiveIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  animation: ${pulseDot} 2s infinite;
  flex-shrink: 0;
`;

const StatChip = styled.div<{ $accent: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0 12px;
  height: 48px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-3px) scale(1.03);
    background: #ffffff;
    border-color: ${(p) => p.$accent}40; 
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.97);
  }

  @media (max-width: 1400px) {
    padding: 0 8px;
    gap: 5px;
  }
`;

const TimeChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 48px;
  background: #f1f5f9;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-3px) scale(1.03);
    background: #ffffff;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.97);
  }
  
  svg { color: #64748b; flex-shrink: 0; }
  @media (max-width: 768px) { width: 100%; justify-content: center; }
`;